import React, { useState, useEffect, useMemo } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  IconButton, 
  Box, 
  Avatar, 
  Menu, 
  MenuItem, 
  styled, 
  Chip,
  Divider,
  useMediaQuery,
  useTheme,
  FormControl,
  Select,
  Tooltip,
  Snackbar,
  Alert,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import LogoutIcon from '@mui/icons-material/Logout';
import LockResetIcon from '@mui/icons-material/LockReset';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import { useAuth } from '../../contexts/AuthContext';
import firebaseDataService from '../../services/FirebaseDataService';
import aiChatService from '../../services/aiChatService';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import NotificationMenu from '../notifications/NotificationMenu';
import { useTabContextSafe } from '../../contexts/TabContext';
import CompanyDetailsModal from '../accounting/invoice/CompanyDetailsModal';
import ChangePasswordModal from './ChangePasswordModal';
import GlobalSearch from './GlobalSearch';
import { BRAND } from '../../config/brand';

const StyledAppBar = styled(AppBar)<{ isCollapsed: boolean }>(({ theme, isCollapsed }) => ({
  backgroundColor: 'var(--sidebar-color, #102648)',
  color: 'var(--sidebar-text-color, white)',
  boxShadow: 'none',
  borderRadius: 0,
  position: 'fixed',
  width: `calc(100% - ${isCollapsed ? 70 : 260}px)`,
  marginLeft: isCollapsed ? 70 : 260,
  zIndex: 99,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  // 1400px altında tam genişlik
  '@media (max-width: 1399px)': {
    width: '100%',
    marginLeft: 0,
  },
}));

const MenuButton = styled(IconButton)(({ theme }) => ({
  marginRight: theme.spacing(2),
  display: 'none',
  color: 'var(--sidebar-text-color, white)',
  // 1400px altında menü butonu göster
  '@media (max-width: 1399px)': {
    display: 'inline-flex',
  },
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
}));

const UserChip = styled(Chip)(({ theme }) => ({
  height: 36, // ✅ Daraltıldı: 42 → 36
  borderRadius: 18, // ✅ BorderRadius orantılı azaltıldı: 21 → 18
  padding: theme.spacing(0.5, 1),
  backgroundColor: 'rgba(255, 255, 255, 0.15)',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  transition: 'all 0.2s ease-in-out',
  '& .MuiChip-label': {
    color: 'var(--sidebar-text-color, white)',
    transition: 'color 0.2s ease-in-out',
  },
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.25)',
    borderColor: 'rgba(255, 255, 255, 0.5)',
    '& .MuiChip-label': {
      color: 'var(--sidebar-text-color, white)',
    },
    '& .MuiIconButton-root': {
      color: 'var(--sidebar-text-color, white)',
    },
  },
}));

interface HeaderProps {
  onToggleSidebar: () => void;
  isCollapsed: boolean;
}

interface UserData {
  firstName: string;
  lastName: string;
  email: string;
  companyName: string;
  companyId: string | null;
}

const Header: React.FC<HeaderProps> = ({ onToggleSidebar, isCollapsed }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [companyDetailsOpen, setCompanyDetailsOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [passwordChangedSnackOpen, setPasswordChangedSnackOpen] = useState(false);
  // AI Kullanımı menü ögesi: 3 AI modundan (paket/ajan/plan) en az biri açıksa gösterilir
  const [aiUsageVisible, setAiUsageVisible] = useState(false);
  const { currentUser, logout, userDetails } = useAuth();

  // own-auth: Firebase `providerData` artik yok (her zaman undefined). Oturum acmis
  // her own-auth kullanicisi email/parola degisimi yapabilir → giris varsa true.
  const isPasswordProvider = useMemo(() => {
    return !!currentUser;
  }, [currentUser]);
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width: 1399px)');
  const navigate = useNavigate();
  const tabContext = useTabContextSafe();
  
  useEffect(() => {
    const fetchUserData = async () => {
      if (currentUser) {
        try {
          const data = await firebaseDataService.getUserData(currentUser.uid);
          if (data) {
            setUserData({
              firstName: data.firstName || '',
              lastName: data.lastName || '',
              email: data.email || '',
              companyName: data.companyName || '',
              companyId: data.companyId || null,
            });
          }
        } catch (error) {
          console.error("Kullanıcı bilgileri alınamadı:", error);
        }
      }
    };
    
    fetchUserData();
  }, [currentUser]);

  useEffect(() => {
    // AI kullanım sayfası admin rolüne açık; diğer rollerde status hiç sorgulanmaz
    if (!currentUser || userDetails?.role !== 'admin') {
      setAiUsageVisible(false);
      return;
    }
    let cancelled = false;
    aiChatService
      .getAiChatStatus()
      .then((status) => {
        if (!cancelled) {
          setAiUsageVisible(status.enabled || status.agentEnabled || status.planEnabled);
        }
      })
      .catch(() => {
        if (!cancelled) setAiUsageVisible(false);
      });
    return () => {
      cancelled = true;
    };
  }, [currentUser, userDetails?.role]);
  
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  
  const handleLogout = async () => {
    try {
      tabContext?.clearUnpinnedTabs();
      await logout();
      handleMenuClose();
      navigate('/login');
    } catch (error) {
      console.error('Çıkış yapılırken hata oluştu:', error);
    }
  };
  
  const handleLanguageChange = (event: any) => {
    const lang = event.target.value as string;
    i18n.changeLanguage(lang);
  };

  const userFullName = userData 
    ? `${userData.firstName} ${userData.lastName}` 
    : 'Kullanıcı';

  return (
    <StyledAppBar elevation={0} isCollapsed={isCollapsed}>
      <Toolbar sx={{ minHeight: { xs: 48, sm: 48 }, height: 56, py: 0 }}>
        {/* Mobile: Profil butonu solda */}
        <IconButton
          onClick={handleMenuOpen}
          sx={{
            display: { xs: 'inline-flex', md: 'none' },
            bgcolor: 'rgba(255, 255, 255, 0.15)',
            color: 'var(--sidebar-text-color, white)',
            width: 36, // ✅ Daraltıldı: 40 → 36
            height: 36, // ✅ Daraltıldı: 40 → 36
            mr: 1,
            border: '1px solid rgba(255, 255, 255, 0.3)',
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.25)',
              borderColor: 'rgba(255, 255, 255, 0.5)',
            }
          }}
        >
          <Avatar 
            sx={{ 
              width: 28, // ✅ Daraltıldı: 32 → 28
              height: 28, // ✅ Daraltıldı: 32 → 28
              bgcolor: 'transparent',
              color: 'inherit',
              fontSize: '0.875rem', // ✅ Font daraltıldı
              fontWeight: 'bold'
            }}
          >
            {userData ? `${userData.firstName[0]}${userData.lastName[0]}` : 'U'}
          </Avatar>
        </IconButton>
        
        <Typography 
          variant={isMobile ? "h6" : "h5"}
          component="h1" 
          sx={{ 
            flex: 1,
            minWidth: 0,
            fontWeight: 'bold', 
            color: 'var(--sidebar-text-color, white)',
            display: 'flex',
            alignItems: 'center',
            fontSize: { xs: '1rem', sm: '1.25rem' } // ✅ Font daraltıldı
          }}
        >
          {userData?.companyName || BRAND.fullName}
        </Typography>

        {/* Site içi global arama — header'ın ortasında (sayfa + hızlı işlem; Ctrl+K) */}
        <Box sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'center', px: 2 }}>
          <GlobalSearch />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
          {/* Dil Seçici - Mobilde daha kompakt */}
          <FormControl 
            variant="outlined" 
            size="small" 
            sx={{ 
              mr: { xs: 0, sm: 1 },
              minWidth: { xs: 60, sm: 110 }
            }}
          >
            <Select
              value={i18n.language.split('-')[0]}
              onChange={handleLanguageChange}
              sx={{
                borderRadius: '20px',
                color: 'var(--sidebar-text-color, white)',
                fontWeight: 'bold',
                fontSize: { xs: '0.75rem', sm: '0.8rem' }, // ✅ Font daraltıldı
                height: 32, // ✅ Yükseklik azaltıldı
                '.MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.6)',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.8)',
                  borderWidth: '1px',
                },
                '.MuiSelect-icon': {
                  color: 'var(--sidebar-text-color, white)',
                },
              }}
            >
              <MenuItem value={'tr'}>
                <Box sx={{ display: { xs: 'none', sm: 'block' } }}>Türkçe</Box>
                <Box sx={{ display: { xs: 'block', sm: 'none' } }}>TR</Box>
              </MenuItem>
              <MenuItem value={'en'}>
                <Box sx={{ display: { xs: 'none', sm: 'block' } }}>English</Box>
                <Box sx={{ display: { xs: 'block', sm: 'none' } }}>EN</Box>
              </MenuItem>
            </Select>
          </FormControl>
          
          <NotificationMenu />
          
          {/* Desktop: İsim + Avatar */}
          <UserChip
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: 'bold',
                    fontSize: '0.875rem', // ✅ Font daraltıldı
                    display: { xs: 'none', md: 'block' }
                  }}
                >
                  {userFullName}
                </Typography>
                <IconButton 
                  size="small" 
                  onClick={handleMenuOpen}
                  sx={{ ml: { xs: 0, md: 0.5 }, p: 0, color: 'var(--sidebar-text-color, white)' }}
                >
                  <ExpandMoreIcon fontSize="small" />
                </IconButton>
              </Box>
            }
            onClick={handleMenuOpen}
            sx={{ 
              cursor: 'pointer',
              display: { xs: 'none', md: 'flex' },
              height: 36, // ✅ Daraltıldı: 42 → 36
            }}
          />

          {/* Mobile: Menü butonu sağda */}
          <MenuButton 
            edge="end" 
            color="inherit" 
            aria-label="menu" 
            onClick={onToggleSidebar}
          >
            <MenuIcon />
          </MenuButton>
          
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            PaperProps={{
              sx: { width: 220, mt: 1 }
            }}
          >
            <MenuItem onClick={() => {
              handleMenuClose();
              navigate('/profile');
            }}>
              <PersonIcon fontSize="small" sx={{ mr: 1.5, color: 'action.active' }} />
              {t('header.profile')}
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleMenuClose();
                setCompanyDetailsOpen(true);
              }}
              disabled={!userData?.companyId}
            >
              <BusinessIcon fontSize="small" sx={{ mr: 1.5, color: 'action.active' }} />
              {t('header.companyDetails')}
            </MenuItem>
            {/* Sifre guncelleme — sadece email/password kullanicilari icin aktif.
                Google kullanicilari icin disabled + Tooltip ile bilgilendirme. */}
            <Tooltip
              title={!isPasswordProvider ? (t('header.changePasswordDisabledTooltip') as string) : ''}
              placement="left"
              arrow
              disableHoverListener={isPasswordProvider}
              disableFocusListener={isPasswordProvider}
            >
              <span>
                <MenuItem
                  onClick={() => {
                    handleMenuClose();
                    setChangePasswordOpen(true);
                  }}
                  disabled={!isPasswordProvider}
                  sx={{ width: '100%' }}
                >
                  <LockResetIcon fontSize="small" sx={{ mr: 1.5, color: 'action.active' }} />
                  {t('header.changePassword')}
                </MenuItem>
              </span>
            </Tooltip>
            {aiUsageVisible && (
              <MenuItem
                onClick={() => {
                  handleMenuClose();
                  navigate('/ai-kullanim');
                }}
              >
                <QueryStatsIcon fontSize="small" sx={{ mr: 1.5, color: 'action.active' }} />
                {t('header.aiUsage')}
              </MenuItem>
            )}
            <Divider />
            <MenuItem onClick={handleLogout}>
              <LogoutIcon fontSize="small" sx={{ mr: 1.5, color: 'error.main' }} />
              {t('header.logout')}
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>

      {/* Firma Bilgileri Modal — Invoice ekranindaki ile ayni component, ayni
          Firebase yolu (companies/{companyId}/info). companyId yoksa hic
          render edilmez. */}
      {userData?.companyId && (
        <CompanyDetailsModal
          open={companyDetailsOpen}
          onClose={(updated) => {
            setCompanyDetailsOpen(false);
            // Modal'da sirket adi degistiyse Header'daki baslik da
            // tazelensin diye userData'yi yeniden yukle.
            if (updated && currentUser) {
              firebaseDataService
                .getUserData(currentUser.uid, false)
                .then((data) => {
                  if (data) {
                    setUserData({
                      firstName: data.firstName || '',
                      lastName: data.lastName || '',
                      email: data.email || '',
                      companyName: data.companyName || '',
                      companyId: data.companyId || null,
                    });
                  }
                })
                .catch((err) => {
                  console.error('Header: companyDetails sonrasi userData refresh hatasi:', err);
                });
            }
          }}
          companyId={userData.companyId}
        />
      )}

      {/* Sifre Guncelleme Modal — Firebase Auth reauthenticate + updatePassword */}
      <ChangePasswordModal
        open={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
        onSuccess={() => setPasswordChangedSnackOpen(true)}
      />

      {/* Sifre guncelleme basari bildirimi */}
      <Snackbar
        open={passwordChangedSnackOpen}
        autoHideDuration={4000}
        onClose={() => setPasswordChangedSnackOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setPasswordChangedSnackOpen(false)}
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {t('changePasswordModal.success')}
        </Alert>
      </Snackbar>
    </StyledAppBar>
  );
};

export default Header; 